import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "src/app-redux/hooks";
import { Link, useHistory } from "react-router-dom";
import {
  Box,
  Link as MuiLink,
  Grid,
  Switch,
  Backdrop,
  TextField,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
} from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { GridRowModel } from "@mui/x-data-grid-pro";
import { useFormik } from "formik";
import moment from "moment";
import "moment-timezone";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "src/components/CommonButton/CommonButton";
import NewCommonButton from "src/components/CommonButton/NewCommonButton";
import CommonDataGridPage from "src/components/CommonDataGridPage/CommonDataGridPage";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { columns } from "./components/columns";
import { getPrintFile, searchData } from "src/app-redux/common-actions";
import { useStyles } from "./components/style";
import { NodCommercialRoute } from "../../nod-commercial.routes";
import {
  deleteCommercialPayement,
  getPaymentDetailsById,
  getPaymentTypesDropdownAction,
  voidCommercialPayement,
} from "src/app-redux/commercial/actions/commercialActions";
import {
  setSelectedPaymentId,
  selectLoader as commercialLoader,
  setAmountDue,
} from "src/app-redux/commercial/commercialSlice";
import Drawer from "./components/Drawer";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import BlockIcon from "@mui/icons-material/Block";
import TransitionDialog from "src/components/AlertDialog/TransitionDialog";
import { MoreVert, PrintSharp, Warning } from "@material-ui/icons";
import { loader as gateEntryLoader } from "src/app-redux/gate-entry/gateEntrySlice";
import { filteryByArr, indexStatusList } from "src/common/utils/app.dropdowns";
import { filterBy } from "src/common/utils/helpers";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import { Tooltip } from "@mui/material";
import DuplicateReceiptDialog from "src/components/AlertDialog/DuplicateReceiptDialog";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import CommonLoader from "src/components/Loader/CommonLoader";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import MultiSelect2 from "src/components/molecules/MultiSelect2";

const INVOICE_CLIMAX_URL = "http://10.14.20.11:5000";

/**
 * Outstanding amount on invoice list / pay drawer.
 * Backend fields are inconsistent across terminals:
 * - `totalPayment` is typically amount BEFORE sales tax
 * - `totalPaymentDue` is typically amount AFTER sales tax
 * Prefer the "due" field when present; otherwise fall back safely.
 */
function getInvoicesNetAmountDisplay(row: any): number {
  const received = Number(row?.totalPaymentReceived) || 0;

  // Backend totals can drift out of sync after "edit invoice" flows (details updated but header not),
  // so prefer the *largest* available "total due" signal to avoid showing 0 pending incorrectly.
  const totalDue = Number(row?.totalPaymentDue);
  const net = Number(row?.netAmount);
  const total = Number(row?.totalPayment);
  const sTax = Number(row?.sTaxAmount);
  const totalPlusTax = Number.isFinite(total) && Number.isFinite(sTax) ? total + sTax : NaN;

  const candidates = [totalDue, net, totalPlusTax, total].filter((n) =>
    Number.isFinite(n)
  ) as number[];
  const bestTotal = candidates.length ? Math.max(...candidates) : 0;

  return Math.max(0, bestTotal - received);
}

function isIndexStatusPending(payment: any): boolean {
  return String(payment?.indexStatus ?? "")
    .toUpperCase()
    .includes("P");
}

function isInvoiceUnpaid(payment: any): boolean {
  if (!payment) return false;
  const status = String(payment.paymentStatus ?? "").trim();
  if (status === "Void" || status === "Paid") return false;
  return true;
}

function canEditInvoice(payment: any): boolean {
  if (!payment) return false;
  const status = String(payment.paymentStatus ?? "").trim();
  if (status === "Void" || status === "Paid") return false;
  return isIndexStatusPending(payment) || isInvoiceUnpaid(payment);
}

function resolveChassisNumber(item: any): string {
  if (typeof item === "string") return item.trim();
  return String(
    item?.chasisNumber ??
      item?.chassisNumber ??
      item?.ChasisNumber ??
      item?.ChassisNumber ??
      item?.name ??
      ""
  ).trim();
}

function resolveChassisContainerNumber(item: any, fallbackContainer = ""): string {
  const fromItem = String(
    item?.containerNumber ??
      item?.ContainerNumber ??
      item?.containerNo ??
      item?.ContainerNo ??
      item?.container ??
      item?.cntrNo ??
      item?.container_number ??
      ""
  ).trim();
  return fromItem || String(fallbackContainer ?? "").trim();
}

function normalizeGatePassChassisOptions(value: any, paymentContainers: string[] = []): any[] {
  if (!value) return [];

  const nestedContainerList =
    value?.containersList ??
    value?.containerList ??
    value?.containers ??
    value?.containerChassisList;
  if (Array.isArray(nestedContainerList) && nestedContainerList.length) {
    return nestedContainerList.flatMap((container: any) => {
      const containerNumber = resolveChassisContainerNumber(container);
      const chassisList =
        container?.chasisNumbers ??
        container?.chassisNumbers ??
        container?.chasisList ??
        container?.chassisList ??
        [];

      return (Array.isArray(chassisList) ? chassisList : []).map((chassis: any) => ({
        chasisNumber: resolveChassisNumber(chassis),
        containerNumber: resolveChassisContainerNumber(chassis, containerNumber),
      }));
    });
  }

  const containerChassisGroups =
    value?.containerChassisNumbers ?? value?.containerChassisList ?? value?.containerChassis;
  if (Array.isArray(containerChassisGroups) && containerChassisGroups.length) {
    return containerChassisGroups.flatMap((item: any) => {
      const containerNumber = resolveChassisContainerNumber(item);
      const chassisList = item?.chassisNumbers ?? item?.chasisNumbers ?? [];

      if (Array.isArray(chassisList) && chassisList.length) {
        return chassisList.map((chassis: any) => ({
          chasisNumber: resolveChassisNumber(chassis),
          containerNumber: resolveChassisContainerNumber(chassis, containerNumber),
        }));
      }

      return [
        {
          chasisNumber: resolveChassisNumber(item),
          containerNumber,
        },
      ];
    });
  }

  const raw = value?.chasisNumbers ?? value?.chassisNumbers;
  if (!Array.isArray(raw)) return [];

  const singleContainerFallback = paymentContainers.length === 1 ? paymentContainers[0] : "";

  return raw
    .map((item: any) => ({
      chasisNumber: resolveChassisNumber(item),
      containerNumber: resolveChassisContainerNumber(item, singleContainerFallback),
    }))
    .filter((item) => item.chasisNumber);
}

function buildContainerChassisNumbersPayload(
  selected: any[],
  allOptions: any[] = [],
  paymentContainers: string[] = []
): { chassisNumber: string; containerNumber: string }[] {
  const optionByChassis = new Map<string, any>();
  (Array.isArray(allOptions) ? allOptions : []).forEach((opt) => {
    const chassisNumber = resolveChassisNumber(opt);
    if (chassisNumber) optionByChassis.set(chassisNumber, opt);
  });

  const singleContainerFallback = paymentContainers.length === 1 ? paymentContainers[0] : "";

  return (Array.isArray(selected) ? selected : [])
    .map((item) => {
      const chassisNumber = resolveChassisNumber(item);
      const source = optionByChassis.get(chassisNumber) ?? item;
      const containerNumber = resolveChassisContainerNumber(source, singleContainerFallback);

      return { chassisNumber, containerNumber };
    })
    .filter((item) => item.chassisNumber);
}

function buildNonBydContainerChassisNumbersPayload(
  paymentContainers: string[] = [],
  selectedContainer = ""
): { chassisNumber: string; containerNumber: string }[] {
  const containers = (
    paymentContainers.length ? paymentContainers : [selectedContainer].filter(Boolean)
  )
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .filter((v, idx, arr) => arr.indexOf(v) === idx);

  return containers.map((containerNumber) => ({
    containerNumber,
    chassisNumber: "",
  }));
}

interface Props {}

export function PaymentsList({}: Props): React.ReactElement {
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";
  const classes = useStyles();
  const terminalType = Number(localStorage.getItem("terminalType"));
  const paymentsListColumns =
    terminalType === 4
      ? columns.filter(
          (column: any) => column.accessorKey !== "nomination" && column.accessorKey !== "gdNumber"
        )
      : columns;
  const [displayColumns, setDisplayColumns] = React.useState<any>(paymentsListColumns);
  const moduleName = NodCommercialRoute.title;
  const [paymentRecord, setpaymentRecord] = React.useState<GridRowModel[]>([]);
  const pageHeading = "Invoices";
  const [loader, setLoader] = useState<any>(false);
  const loader2: any = useAppSelector(commercialLoader);
  const [isCustomDate, setIsCustomDate] = useState<boolean>(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const terminalPageLink = NodCommercialRoute.subRoutes
    ? `${NodCommercialRoute.path}${NodCommercialRoute?.subRoutes[0].path}`
    : "";
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isDrawerOpen, setisDrawerOpen] = React.useState(false);
  const [isOpenDeleteDailog, setIsOpenDeleteDailog] = useState<boolean>(false);
  const [isOpenVoidDailog, setIsOpenVoidDailog] = useState<boolean>(false);
  const [deleteGridItem, setdeleteGridItem] = useState<any>("");
  const receiptLoader: any = useAppSelector(gateEntryLoader);
  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);
  const [deleteRemarks, setDeleteRemarks] = useState("");
  const [isDeleteMode, setIsDeleteMode] = React.useState(false);
  const [listAction, setListAction] = useState<any>(null);
  const isBydGatePassInvoice = String(listAction?.invoiceType ?? "").toUpperCase() === "BYD";
  const canPrintGatePass =
    isBydGatePassInvoice || listAction?.paymentStatus === "Paid" || listAction?.gmApproval === true;
  const handleCloseDrawer = () => {
    handleSearch();
    setisDrawerOpen(false);
  };

  const handleClickActionBtn = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseActionBtn: () => void = () => {
    setAnchorEl(null);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteMode(false);
    setDeleteRemarks("");
    setIsOpenDeleteDailog(false);
  };
  const handleDeleteDialog = async () => {
    setIsDeleteMode(true);
    if (deleteRemarks) {
      setIsOpenDeleteDailog(false);
      if (deleteGridItem) {
        const body = { ...deleteGridItem, deleteRemarks: deleteRemarks };

        const res: any = await dispatch(deleteCommercialPayement(body));
        if (res.payload) {
          handleSearch();
        }
      }
    }
  };
  const handleCloseVoidDialog = () => {
    setIsDeleteMode(false);
    setDeleteRemarks("");
    setIsOpenVoidDailog(false);
  };
  const handleVoidDialog = async () => {
    setIsOpenVoidDailog(false);
    const res: any = await dispatch(voidCommercialPayement(listAction?.paymentId));
    if (res.payload) {
      handleSearch();
    }
  };

  const handleDelete = (id: any) => {
    setIsOpenDeleteDailog(true);
    setdeleteGridItem(id);
  };

  const handleVoid = () => {
    setIsOpenVoidDailog(true);
  };

  const openPaymentEditDrawer = (payment: any) => {
    if (!payment?.paymentId) return;
    dispatch(getPaymentDetailsById(payment.paymentId));
    dispatch(
      setSelectedPaymentId({
        paymentId: payment.paymentId,
        regNo: payment.registrationNumber,
        paymentStatus: payment.paymentStatus,
        containerNo: payment.containerNo,
        virNumber: payment.virNo,
        indexNumber: payment.indexNo,
      })
    );
    dispatch(setAmountDue(getInvoicesNetAmountDisplay(payment)));
    dispatch(getPaymentTypesDropdownAction());
    setisDrawerOpen(true);
  };

  const openPaymentInGenerateInvoices = async (payment: any) => {
    try {
      const paymentId = payment?.paymentId;
      if (!paymentId) return;

      setLoader(true);
      const res: any = await fetchWrapper.get(`get-payment/${paymentId}`);
      if (!res?.isSuccess) {
        dispatch(
          addNotificationAction({
            message: res?.error || "Unable to fetch payment details",
            options: { variant: "error" },
          })
        );
        return;
      }

      history.push(`${NodCommercialRoute.path}/generate-invoices`, {
        editPayment: res?.value,
        paymentId,
        listPayment: payment,
        editOpenedAt: Date.now(),
      });
    } catch (e: any) {
      dispatch(
        addNotificationAction({
          message: e?.message || "Unable to fetch payment details",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (paymentRecord.length) {
      const transformedColumns: any = paymentsListColumns.map((column: any) =>
        column.accessorKey === "serialNumber"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <MuiLink
                      className="customMuiLink"
                      onClick={() => {
                        openPaymentEditDrawer(row.original);
                      }}
                    >
                      {row.original.serialNumber ? row.original.serialNumber : "-"}
                    </MuiLink>
                  </>
                );
              },
            }
          : column.accessorKey === "vehicleStatus"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <>
                    <Box
                      className={`${
                        row.original.vehicleStatus === "Checked-out" ? "label-green" : ""
                      }`}
                    >
                      {row.original.vehicleStatus}
                    </Box>
                  </>
                );
              },
            }
          : column.accessorKey === "nomination"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue }: any) {
                return (
                  <Tooltip title={renderedCellValue} followCursor>
                    <span>{renderedCellValue}</span>
                  </Tooltip>
                );
              },
            }
          : column.accessorKey === "totalPayment"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return ` ${
                  row.original.totalPayment ? row.original.currency.toUpperCase() || "PKR " : ""
                } ${
                  row.original.totalPayment
                    ? row.original.totalPayment.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    : ""
                }`;
              },
            }
          : column.accessorKey === "netAmount"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                const displayNet = getInvoicesNetAmountDisplay(row.original);
                const cur = String(row.original?.currency || "PKR").toUpperCase();
                return ` ${cur} ${displayNet.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}`;
              },
            }
          : column.accessorKey === "sTaxAmount"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return ` ${
                  row.original?.sTaxAmount ? row.original.currency.toUpperCase() || "PKR " : ""
                } ${
                  row.original?.sTaxAmount
                    ? row.original?.sTaxAmount?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    : ""
                }`;
              },
            }
          : column.accessorKey === "discount"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return ` ${
                  row.original?.discount ? row.original.currency.toUpperCase() || "PKR " : ""
                } ${
                  row.original?.discount
                    ? row.original?.discount?.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    : ""
                }`;
              },
            }
          : column.accessorKey === "totalPaymentReceived"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return ` ${
                  row.original.totalPaymentReceived
                    ? row.original.currency.toUpperCase() || "PKR "
                    : ""
                } ${
                  row.original.totalPaymentReceived
                    ? row.original.totalPaymentReceived.toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                      })
                    : ""
                }`;
              },
            }
          : column.accessorKey === "remarks"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue }: any) {
                return (
                  <Tooltip title={renderedCellValue} followCursor>
                    <span>{renderedCellValue}</span>
                  </Tooltip>
                );
              },
            }
          : column.accessorKey === "actionButtons"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return row.original.paymentStatus == "Void" ? (
                  <>
                    <Tooltip title={"This entry has been marked as void."} placement="bottom-start">
                      <span className={classes.customButton}>
                        <NewCommonButton
                          style={{ minWidth: "30px", padding: "4px" }}
                          icon={<Warning />}
                        >
                          {/* Marked as Wrong Entry */}
                        </NewCommonButton>
                      </span>
                    </Tooltip>
                  </>
                ) : (
                  <React.Fragment>
                    <Box>
                      <Tooltip title="More">
                        <Button
                          onClick={(e) => {
                            setListAction(row.original);
                            handleClickActionBtn(e);
                          }}
                          disabled={row.original?.paymentStatus == "Void"}
                          aria-controls={open ? "account-menu" : undefined}
                          aria-haspopup="true"
                          aria-expanded={open ? "true" : undefined}
                          style={{ minWidth: "30px", padding: "4px" }}
                        >
                          <MoreVert />
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [paymentRecord]);

  const formik = useFormik({
    initialValues: {
      inProgressVehiclesDataOnly: true,
      filterBy: "today",
      vehicleStatusId: 10,
      startDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(0, 0)).format(
        "YYYY-MM-DDTHH:mm"
      ),
      endDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(23, 59)).format(
        "YYYY-MM-DDTHH:mm"
      ),
    },
    onSubmit: () => {
      // handleSearch();
    },
  });
  const { values, setValues } = formik;
  const handleSearch = async () => {
    const { filterBy, startDate, endDate, inProgressVehiclesDataOnly } = values;
    const body = {
      filterBy: filterBy || null,
      startDate: startDate || null,
      endDate: endDate || null,
      inProgressVehiclesDataOnly: inProgressVehiclesDataOnly || false,
    };
    setpaymentRecord([]);
    setLoader(true);
    try {
      const results: any = await dispatch(searchData("/get-all-payments", JSON.stringify(body)));
      const apiRecord = results;
      if (apiRecord) {
        const transformedData = apiRecord.map((data: any) => {
          return { ...data, id: data.paymentId ? data.paymentId : Math.random() };
        });
        setpaymentRecord(transformedData);
      }
    } catch (error) {
      setLoader(false);
    }
    setLoader(false);
  };
  const [resetHandler, setResetHandler] = useState(false);

  const resetSearch = () => {
    setValues(formik.initialValues);
    setIsCustomDate(false);
    setResetHandler(true);
  };
  useEffect(() => {
    formik.setFieldValue("filterBy", "today");
    handleSearch();
    setResetHandler(false);
  }, [resetHandler]);
  const filterByAction = (data: any) => {
    formik.setFieldValue("filterBy", data.value);
    if (data && data.value) {
      const res = filterBy(data);
      if (res?.isCustomDate) {
        setIsCustomDate(true);
        formik.setFieldValue("startDateCustom", res?.startDate);
        formik.setFieldValue("endDateCustom", res?.endDate);
      } else {
        setIsCustomDate(false);
      }
      formik.setFieldValue("startDate", res?.startDate);
      formik.setFieldValue("endDate", res?.endDate);
    }
  };

  const [isOpenPrintDailog, setIsOpenPrintDailog] = useState<boolean>(false);
  const [printType, setPrintType] = useState<any>(0);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [gatePassWeight, setGatePassWeight] = useState<string>("");
  const [nlcSealNumber, setNlcSealNumber] = useState<string>("");
  const [indexStatus, setIndexStatus] = useState<string>("");
  const [indexStatusError, setIndexStatusError] = useState(false);
  const [gatePassContainerNumber, setGatePassContainerNumber] = useState<string>("");
  const [gatePassContainerNumbersApi, setGatePassContainerNumbersApi] = useState<string[]>([]);
  const [isGatePassContainersLoading, setIsGatePassContainersLoading] = useState<boolean>(false);
  const [gatePassIndexNumber, setGatePassIndexNumber] = useState<string>("");
  const [gatePassVirIndexesApi, setGatePassVirIndexesApi] = useState<string[]>([]);
  const [gatePassReleasedVirIndexesList, setGatePassReleasedVirIndexesList] = useState<any[]>([]);
  const [isGatePassVirIndexesLoading, setIsGatePassVirIndexesLoading] = useState<boolean>(false);
  const [gatePassVehicleId, setGatePassVehicleId] = useState<string>("");
  const [gatePassVehicleError, setGatePassVehicleError] = useState(false);
  const [gatePassChassisError, setGatePassChassisError] = useState(false);
  const [gateOutContainerNumber, setGateOutContainerNumber] = useState<string>("");
  const [gateOutVehicleList, setGateOutVehicleList] = useState<any[]>([]);
  const [gateOutContainerList, setGateOutContainerList] = useState<any[]>([]);
  const [isGateOutDataLoading, setIsGateOutDataLoading] = useState<boolean>(false);
  const [gatePassChassisNumber, setGatePassChassisNumber] = useState<string>("");
  const [gatePassChassisOptions, setGatePassChassisOptions] = useState<any[]>([]);
  const [gatePassSelectedChassis, setGatePassSelectedChassis] = useState<any[]>([]);
  const [isGatePassChassisLoading, setIsGatePassChassisLoading] = useState<boolean>(false);
  const gatePassChassisFetchKeyRef = useRef("");

  function getContainerNumberOptions(row: any): string[] {
    const raw = row?.containerNumbers ?? row?.containerNo ?? "";
    const values = Array.isArray(raw)
      ? raw
      : String(raw)
          .split(/[,\n]/g)
          .map((v) => v.trim())
          .filter(Boolean);

    return (values || [])
      .map((v: any) => String(v ?? "").trim())
      .filter(Boolean)
      .filter((v: string, idx: number, arr: string[]) => arr.indexOf(v) === idx);
  }

  const gatePassContainerOptions = (
    gatePassContainerNumbersApi?.length
      ? gatePassContainerNumbersApi
      : getContainerNumberOptions(listAction)
  ).map((cn) => ({ name: cn }));

  const fetchGatePassContainersByPaymentId = async (paymentId: any) => {
    const id = String(paymentId ?? "").trim();
    if (!id) {
      setGatePassContainerNumbersApi([]);
      return;
    }

    setIsGatePassContainersLoading(true);
    try {
      const res: any = await fetchWrapper.get(
        `get-all-containers-by-payment-id/${encodeURIComponent(id)}`
      );
      if (res?.isSuccess) {
        const raw = res?.value ?? [];
        const list = (Array.isArray(raw) ? raw : [])
          .map((v: any) =>
            String(v?.containerNo ?? v?.containerNumber ?? v?.name ?? v ?? "").trim()
          )
          .filter(Boolean)
          .filter((v: string, idx: number, arr: string[]) => arr.indexOf(v) === idx);
        setGatePassContainerNumbersApi(list);
      } else {
        setGatePassContainerNumbersApi([]);
      }
    } catch {
      setGatePassContainerNumbersApi([]);
    } finally {
      setIsGatePassContainersLoading(false);
    }
  };

  const gatePassIndexOptions = (
    gatePassVirIndexesApi?.length
      ? gatePassVirIndexesApi
      : [String(listAction?.indexNo ?? "").trim()].filter(Boolean)
  ).map((idx) => ({ name: idx }));

  const getGatePassBillNumber = () => {
    const containerNumber = String(gatePassContainerNumber ?? "").trim();
    const indexNumber = String(gatePassIndexNumber ?? "").trim();
    const list = gatePassReleasedVirIndexesList;

    if (!list?.length) {
      return String(listAction?.billNumber ?? "").trim();
    }

    const match =
      list.find((item: any) => {
        const itemContainer = String(item?.containerNumber ?? "").trim();
        const itemIndex = String(item?.indexNumber ?? item?.indexNo ?? "").trim();
        if (containerNumber && indexNumber) {
          return itemContainer === containerNumber && itemIndex === indexNumber;
        }
        if (containerNumber) return itemContainer === containerNumber;
        return false;
      }) ??
      list.find((item: any) => {
        const itemIndex = String(item?.indexNumber ?? item?.indexNo ?? "").trim();
        return indexNumber && itemIndex === indexNumber;
      }) ??
      list[0];

    return String(match?.billNumber ?? listAction?.billNumber ?? "").trim();
  };

  const getGatePassPaymentContainers = () => {
    const localFallback = getContainerNumberOptions(listAction);
    return (gatePassContainerNumbersApi?.length ? gatePassContainerNumbersApi : localFallback)
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
  };

  const fetchReleasedVirIndexes = async (virNo: any) => {
    const vir = String(virNo ?? "").trim();
    if (!vir) {
      setGatePassVirIndexesApi([]);
      setGatePassReleasedVirIndexesList([]);
      return;
    }

    setIsGatePassVirIndexesLoading(true);
    try {
      const res: any = await fetchWrapper.get(
        `get-released-vir-indexes/${encodeURIComponent(vir)}`
      );
      if (res?.isSuccess) {
        const raw = res?.value?.indexesList ?? res?.value ?? [];
        const indexes = Array.isArray(raw) ? raw : [];
        const list = indexes
          .map((v: any) =>
            String(
              v?.indexNumber ?? v?.indexNo ?? v?.indexNumberId ?? v?.index ?? v?.name ?? v ?? ""
            ).trim()
          )
          .filter(Boolean)
          .filter((v: string, idx: number, arr: string[]) => arr.indexOf(v) === idx);
        setGatePassReleasedVirIndexesList(indexes);
        setGatePassVirIndexesApi(list);
      } else {
        setGatePassVirIndexesApi([]);
        setGatePassReleasedVirIndexesList([]);
      }
    } catch {
      setGatePassVirIndexesApi([]);
      setGatePassReleasedVirIndexesList([]);
    } finally {
      setIsGatePassVirIndexesLoading(false);
    }
  };

  const fetchGateOutVehiclesAndContainers = async () => {
    setIsGateOutDataLoading(true);
    try {
      const results: any = await fetchWrapper.get("get-all-vehicles-and-containers-for-gate-out");
      if (results?.isSuccess) {
        setGateOutVehicleList(results.value?.vehicleList || []);
        setGateOutContainerList(results.value?.containerList || []);
      } else {
        setGateOutVehicleList([]);
        setGateOutContainerList([]);
      }
    } catch {
      setGateOutVehicleList([]);
      setGateOutContainerList([]);
    } finally {
      setIsGateOutDataLoading(false);
    }
  };

  const fetchGatePassChassisByIndex = async () => {
    const virNumber = String(listAction?.virNo ?? "").trim();
    const containerNumber = String(gatePassContainerNumber ?? "").trim();
    const indexNumber = String(gatePassIndexNumber ?? "").trim();
    const localFallback = getContainerNumberOptions(listAction);
    const allContainerNumbers = (
      gatePassContainerNumbersApi?.length ? gatePassContainerNumbersApi : localFallback
    )
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    const containerNumbersParam = allContainerNumbers.length
      ? allContainerNumbers.join(",")
      : containerNumber;

    if (!virNumber || !containerNumbersParam || !indexNumber) {
      setGatePassChassisOptions([]);
      setGatePassSelectedChassis([]);
      setGatePassChassisNumber("");
      return;
    }

    setIsGatePassChassisLoading(true);
    try {
      const fetchChassisForContainer = async (cn: string) => {
        const response: any = await fetchWrapper.get(
          `get-all-containers-index_chasis_numbers/${encodeURIComponent(
            cn
          )}?virNo=${encodeURIComponent(virNumber)}&indexNumber=${encodeURIComponent(indexNumber)}`
        );

        if (!response?.isSuccess) return [];

        return normalizeGatePassChassisOptions(response?.value, [cn]).map((opt) => ({
          ...opt,
          containerNumber: resolveChassisContainerNumber(opt, cn),
        }));
      };

      let opts: any[] = [];
      if (allContainerNumbers.length > 1) {
        const batches = await Promise.all(
          allContainerNumbers.map((cn) => fetchChassisForContainer(cn))
        );
        opts = batches.flat();
      } else {
        const response: any = await fetchWrapper.get(
          `get-all-containers-index_chasis_numbers/${encodeURIComponent(
            containerNumbersParam
          )}?virNo=${encodeURIComponent(virNumber)}&indexNumber=${encodeURIComponent(indexNumber)}`
        );

        if (response?.isSuccess) {
          opts = normalizeGatePassChassisOptions(response?.value, allContainerNumbers).map(
            (opt) => ({
              ...opt,
              containerNumber: resolveChassisContainerNumber(
                opt,
                allContainerNumbers[0] || containerNumber
              ),
            })
          );
        }
      }

      setGatePassChassisOptions(opts);
    } catch {
      setGatePassChassisOptions([]);
    } finally {
      setIsGatePassChassisLoading(false);
    }
  };

  useEffect(() => {
    if (isOpenPrintDailog && printType === 3 && terminalType !== 4) {
      setGatePassWeight("0");
      setNlcSealNumber("");
      setIndexStatus("");
      setIndexStatusError(false);
      setGatePassContainerNumbersApi([]);
      fetchGatePassContainersByPaymentId(listAction?.paymentId);
      setGatePassVirIndexesApi([]);
      setGatePassReleasedVirIndexesList([]);
      fetchReleasedVirIndexes(listAction?.virNo);
      setGatePassIndexNumber(String(listAction?.indexNo ?? "").trim());
      setGatePassVehicleId(String(listAction?.vehicleId ?? "").trim());
      setGatePassVehicleError(false);
      setGatePassChassisError(false);
      setGatePassChassisNumber(String(listAction?.chassisNumber ?? "").trim());
      setGatePassChassisOptions([]);
      setGatePassSelectedChassis([]);
      gatePassChassisFetchKeyRef.current = "";
      setGateOutContainerNumber("");
      setGateOutVehicleList([]);
      setGateOutContainerList([]);
      fetchGateOutVehiclesAndContainers();
    }
  }, [isOpenPrintDailog, listAction, printType, terminalType]);

  useEffect(() => {
    if (!(isOpenPrintDailog && printType === 3 && terminalType !== 4)) return;

    const localFallback = getContainerNumberOptions(listAction);
    const available = gatePassContainerNumbersApi?.length
      ? gatePassContainerNumbersApi
      : localFallback;

    const preferred = String(listAction?.containerNo ?? "").trim();
    const next =
      (preferred && available.includes(preferred) && preferred) ||
      String(available?.[0] ?? "").trim() ||
      "";

    setGatePassContainerNumber(next);
  }, [gatePassContainerNumbersApi, isOpenPrintDailog, listAction, printType, terminalType]);

  useEffect(() => {
    if (!(isOpenPrintDailog && printType === 3 && terminalType !== 4)) return;

    const preferred = String(listAction?.indexNo ?? "").trim();
    const available = gatePassVirIndexesApi?.length
      ? gatePassVirIndexesApi
      : [preferred].filter(Boolean);

    const next =
      (preferred && available.includes(preferred) && preferred) ||
      String(available?.[0] ?? "").trim() ||
      "";

    setGatePassIndexNumber(next);
  }, [gatePassVirIndexesApi, isOpenPrintDailog, listAction, printType, terminalType]);

  useEffect(() => {
    if (!(isOpenPrintDailog && printType === 3 && terminalType !== 4)) return;
    if (String(listAction?.invoiceType ?? "").toUpperCase() !== "BYD") return;
    if (isGatePassContainersLoading || isGatePassVirIndexesLoading) return;

    const virNumber = String(listAction?.virNo ?? "").trim();
    const indexNumber = String(gatePassIndexNumber ?? "").trim();
    const localFallback = getContainerNumberOptions(listAction);
    const allContainerNumbers = (
      gatePassContainerNumbersApi?.length ? gatePassContainerNumbersApi : localFallback
    )
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    const containerNumbersParam = allContainerNumbers.length ? allContainerNumbers.join(",") : "";

    if (!virNumber || !containerNumbersParam || !indexNumber) return;

    const fetchKey = `${virNumber}|${indexNumber}|${containerNumbersParam}`;
    if (gatePassChassisFetchKeyRef.current === fetchKey) return;
    gatePassChassisFetchKeyRef.current = fetchKey;

    fetchGatePassChassisByIndex();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gatePassContainerNumbersApi,
    gatePassIndexNumber,
    isOpenPrintDailog,
    isGatePassContainersLoading,
    isGatePassVirIndexesLoading,
    listAction,
    printType,
    terminalType,
  ]);

  const printReceipt = async (isDuplicate: string) => {
    const isDp = terminalType === 4;
    const isNod = terminalType !== 4;
    setIsOpenPrintDailog(false);
    const dateTime = moment().format("DD MMM YYYY, HH:mm");
    if (printType === 1) {
      const fileMetaData = {
        url: `${process.env.REACT_APP_API_URL}/get-commercial-slip-by-gate-pass-id/${selectedRecordId}?isDuplicate=${isDuplicate}&printDateTime=${dateTime}&isNod=${isNod}&isDp=${isDp}`,
        contentType: ".pdf",
        fileName: "testPdf.pdf",
      };
      await dispatch(getPrintFile(fileMetaData, dispatch));
      return;
    }
    if (printType === 2) {
      const fileMetaData = {
        url: `${process.env.REACT_APP_API_URL}/get-delivery_order_by_payment_id/${selectedRecordId}?isDuplicate=${isDuplicate}&index=${listAction?.indexNo}&containerNumber=${listAction?.containerNo}&virNumber=${listAction?.virNo}`,
        contentType: ".pdf",
        fileName: "testPdf.pdf",
      };
      await dispatch(getPrintFile(fileMetaData, dispatch));
      return;
    }
    if (printType === 3) {
      if (terminalType === 4) {
        // DP Gate Pass Print endpoint
        const fileMetaData = {
          url: `${process.env.REACT_APP_API_URL}/get-dp-gate-pass-invoice-print/${
            listAction?.gatePassId
          }?isDuplicate=${isDuplicate}&gatePassPaymentId=${selectedRecordId}&vehicleNo=${
            listAction?.registrationNumber || ""
          }&PrintDateTime=${dateTime}`,
          contentType: ".pdf",
          fileName: "testPdf.pdf",
        };
        await dispatch(getPrintFile(fileMetaData, dispatch));
        return;
      } else {
        // Original Gate Pass Print endpoint
        const indexNoRaw = listAction?.indexNo ?? "";
        const printBody: Record<string, unknown> = {
          isDuplicate,
          indexNo: indexNoRaw !== "" && indexNoRaw != null ? Number(indexNoRaw) : indexNoRaw,
          virNo: listAction?.virNo ?? "",
          gatePassId: Number(listAction?.gatePassId),
          gatePassPaymentId: Number(selectedRecordId),
        };
        const fileMetaData = {
          url: `${process.env.REACT_APP_API_URL}/get-gate-pass-invoice-print`,
          contentType: ".pdf",
          fileName: "testPdf.pdf",
          method: "POST" as const,
          body: printBody,
        };
        await dispatch(getPrintFile(fileMetaData, dispatch));
        return;
      }
    }
  };

  const printGatePassInvoice = async () => {
    const weight = gatePassWeight.trim() || "0";
    const sealNoRaw = (nlcSealNumber ?? "").trim();
    const idxStatus = String(indexStatus ?? "").trim();
    const cn = String(gatePassContainerNumber ?? "").trim();
    const indexNo = String(gatePassIndexNumber ?? "").trim();
    const vehicleId = String(gatePassVehicleId ?? "").trim();
    const gateOutCn = String(gateOutContainerNumber ?? "").trim();
    const isByd = String(listAction?.invoiceType ?? "").toUpperCase() === "BYD";
    const paymentContainers = getGatePassPaymentContainers();
    const containerChassisNumbers = isByd
      ? buildContainerChassisNumbersPayload(
          gatePassSelectedChassis,
          gatePassChassisOptions,
          paymentContainers
        )
      : buildNonBydContainerChassisNumbersPayload(paymentContainers, cn);
    const billNumber = getGatePassBillNumber();

    if (!isByd && !vehicleId) {
      dispatch(
        addNotificationAction({
          message: "Vehicle Number is required",
          options: { variant: "error" },
        })
      );
      setGatePassVehicleError(true);
      return;
    }
    setGatePassVehicleError(false);

    if (isByd && !containerChassisNumbers.length) {
      dispatch(
        addNotificationAction({
          message: "Chassis Number is required",
          options: { variant: "error" },
        })
      );
      setGatePassChassisError(true);
      return;
    }
    setGatePassChassisError(false);

    if (!idxStatus) {
      dispatch(
        addNotificationAction({
          message: "Index Status is required",
          options: { variant: "error" },
        })
      );
      setIndexStatusError(true);
      return;
    }
    setIndexStatusError(false);
    setIsOpenPrintDailog(false);

    const indexNoRaw = indexNo;
    const vehicleIdRaw = vehicleId;
    const gatePassPaymentIdRaw = selectedRecordId;
    const chassisNumbers = containerChassisNumbers
      .map((item) => item.chassisNumber)
      .filter(Boolean)
      .join(", ");
    const printBody: Record<string, unknown> = {
      toSave: true,
      virNo: listAction?.virNo ?? "",
      indexNo: indexNoRaw !== "" && indexNoRaw != null ? Number(indexNoRaw) : indexNoRaw,
      gatePassId: Number(listAction?.gatePassId),
      gatePassPaymentId:
        gatePassPaymentIdRaw !== "" && gatePassPaymentIdRaw != null
          ? Number(gatePassPaymentIdRaw)
          : gatePassPaymentIdRaw,
      indexStatus: idxStatus,
      gatePassWeight: Number(weight) || 0,
      nlcSealNumber: sealNoRaw,
      containerNumber: cn,
      vehicleId:
        vehicleIdRaw !== "" && vehicleIdRaw != null && vehicleIdRaw !== undefined
          ? Number(vehicleIdRaw)
          : null,
      gateOutContainerNumber: gateOutCn,
      containerChassisNumbers,
      ...(chassisNumbers
        ? {
            chassisNumber: chassisNumbers,
            billNumber,
          }
        : {}),
    };

    const fileMetaData = {
      url: `${process.env.REACT_APP_API_URL}/get-gate-pass-invoice-print`,
      contentType: ".pdf",
      fileName: "testPdf.pdf",
      method: "POST" as const,
      body: printBody,
    };
    await dispatch(getPrintFile(fileMetaData, dispatch));
  };

  const exportHandler = async (row: any) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );
    const exportedRecords = await row?.map((data: any) => {
      return {
        "Serial #": data?.serialNumber,
        "Invoice Date": data?.invoiceDate
          ? moment(new Date(data.invoiceDate)).format("DD-MM-YYYY")
          : "",
        "BL Number": data?.billNumber || "",
        "Payment Date": data?.paymentDate
          ? moment(new Date(data.paymentDate)).format("DD-MM-YYYY")
          : "",
        Vehicle: data?.registrationNumber,
        Driver: data?.driverName,
        Type: data?.vehicleType,
        Status: data?.vehicleStatus,
        Weight: `${data?.weight?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })} ${data.weightUnit ? data.weightUnit : "KG"}`,
        "Type of Payments": data?.paymentTypes,
        "Total Payment": `PKR ${data?.totalPayment?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        "Net Amount": `PKR ${getInvoicesNetAmountDisplay(data).toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        "Tax Amount": `PKR ${data?.sTaxAmount?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        Discount: `PKR ${data?.discount?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        "Payment Received": `PKR ${data?.totalPaymentReceived?.toLocaleString(undefined, {
          maximumFractionDigits: 2,
        })}`,
        "Payment Status": data?.paymentStatus,
        Consignment: data?.consignmentType,
        "GD #": data?.consignmentType,
        Remarks: data?.remarks,
      };
    });
    return exportedRecords;
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={loader2 || receiptLoader}>
        <CommonLoader />
      </Backdrop>
      <Drawer
        isDrawerOpen={isDrawerOpen}
        handleCloseDrawer={() => {
          handleCloseDrawer();
        }}
      />

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        className={classes.menu}
        onClose={handleCloseActionBtn}
        onClick={handleCloseActionBtn}
      >
        {canEditInvoice(listAction) && (
          <MenuItem
            onClick={() => {
              openPaymentInGenerateInvoices(listAction);
              handleCloseActionBtn();
            }}
          >
            <ListItemIcon style={{ minWidth: "40px", color: "#1976d2" }}>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            Edit
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setPrintType(1); // 1 for invoice
            setSelectedRecordId(listAction?.paymentId);
            setIsOpenPrintDailog(true);
            handleCloseActionBtn();
          }}
        >
          <ListItemIcon style={{ minWidth: "40px", color: "#1976d2" }}>
            <PrintSharp fontSize="small" />
          </ListItemIcon>
          Print Invoice
        </MenuItem>
        <>
          {/* Delivery Order - NOT for terminalType 4 */}
          {terminalType !== 4 && (
            <MenuItem
              onClick={() => {
                setPrintType(2); // 2 for delivery order
                setSelectedRecordId(listAction?.paymentId);
                setIsOpenPrintDailog(true);
                handleCloseActionBtn();
              }}
              // disabled={!isBydGatePassInvoice && listAction?.paymentStatus !== "Paid"}
              disabled={!canPrintGatePass}
            >
              <ListItemIcon style={{ minWidth: "40px", color: "#1976d2" }}>
                <PrintSharp fontSize="small" />
              </ListItemIcon>
              Print Delivery Order
            </MenuItem>
          )}

          {/* Gate Pass - allowed for terminalType 4 */}
          <MenuItem
            onClick={() => {
              setPrintType(3); // 3 for gate pass
              setSelectedRecordId(listAction?.paymentId);
              setIsOpenPrintDailog(true);
              handleCloseActionBtn();
            }}
            disabled={!canPrintGatePass}
          >
            <ListItemIcon style={{ minWidth: "40px", color: "#1976d2" }}>
              <PrintSharp fontSize="small" />
            </ListItemIcon>
            Print Gate Pass
          </MenuItem>
        </>

        <MenuItem
          onClick={() => {
            handleDelete(listAction);
            handleCloseActionBtn();
          }}
          // disabled={listAction?.vehicleStatus == "Checked-out"}
        >
          <ListItemIcon style={{ minWidth: "40px", color: "red" }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          Delete
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleVoid();
            handleCloseActionBtn();
          }}
          // disabled={listAction?.vehicleStatus == "Checked-out"}
        >
          <ListItemIcon style={{ minWidth: "40px", color: "red" }}>
            <BlockIcon fontSize="small" />
          </ListItemIcon>
          Mark as Void
        </MenuItem>
      </Menu>

      {/* Dialog for Print Receipt */}
      <DuplicateReceiptDialog
        isOpen={isOpenPrintDailog}
        handleOriginal={() => {
          printReceipt("0");
        }}
        heading={"Confirmation"}
        handleDuplicate={() => {
          printReceipt("1");
        }}
        actions={
          printType === 3 && terminalType !== 4
            ? [{ label: "Gate Pass", onClick: () => printGatePassInvoice() }]
            : undefined
        }
        onClose={() => {
          setIsOpenPrintDailog(false);
        }}
        contentClassName={
          printType === 3 && terminalType !== 4 ? classes.gatePassPrintDialogContent : undefined
        }
        paperClassName={
          printType === 3 && terminalType !== 4 ? classes.gatePassPrintDialogPaper : undefined
        }
      >
        Which receipt do you want to print?
        {printType === 3 && terminalType !== 4 && (
          <Box mt={2} className={classes.gatePassPrintDialogForm}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  fullWidth
                  className="customTextField"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label="VIR Number"
                  name="virNumber"
                  value={String(listAction?.virNo ?? "")}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              {!isBydGatePassInvoice && (
                <Grid item xs={12} md={6}>
                  <CommonAutoCompleteField
                    cssClassName="customTextField"
                    label="Container Number"
                    placeholder="Select Container Number"
                    name="containerNumber"
                    disableClearable={true}
                    optionsType={gatePassContainerOptions}
                    getOptionLabel={(option: any) => option?.name || ""}
                    value={
                      gatePassContainerOptions.find(
                        (opt) => opt.name === gatePassContainerNumber
                      ) || null
                    }
                    onChange={(option: any) => {
                      setGatePassContainerNumber(String(option?.name ?? ""));
                    }}
                    loading={isGatePassContainersLoading}
                  />
                </Grid>
              )}

              {isBydGatePassInvoice && (
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small"
                    fullWidth
                    className="customTextField"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    label="BL Number"
                    name="billNumber"
                    value={getGatePassBillNumber()}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={isBydGatePassInvoice ? 6 : 4}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Index Number"
                  placeholder="Select Index Number"
                  name="indexNumber"
                  disableClearable={true}
                  optionsType={gatePassIndexOptions}
                  getOptionLabel={(option: any) => option?.name || ""}
                  value={
                    gatePassIndexOptions.find((opt) => opt.name === gatePassIndexNumber) || null
                  }
                  onChange={(option: any) => {
                    setGatePassIndexNumber(String(option?.name ?? ""));
                  }}
                  loading={isGatePassVirIndexesLoading}
                />
              </Grid>
              <Grid item xs={12} md={isBydGatePassInvoice ? 6 : 4}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Gate Out Container"
                  placeholder="Select Gate Out Container Number"
                  name="gateOutContainerNumber"
                  optionsType={gateOutContainerList}
                  getOptionLabel={(option: any) => option?.containerNumber || ""}
                  value={
                    gateOutContainerList.find(
                      (opt: any) => opt?.containerNumber === gateOutContainerNumber
                    ) || null
                  }
                  onChange={(option: any) => {
                    setGateOutContainerNumber(String(option?.containerNumber ?? ""));
                  }}
                  loading={isGateOutDataLoading}
                />
              </Grid>
              <Grid item xs={12} md={isBydGatePassInvoice ? 6 : 4}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Vehicle Number"
                  placeholder="Select Vehicle Number"
                  name="vehicleNumber"
                  required={!isBydGatePassInvoice}
                  error={gatePassVehicleError}
                  helperText={gatePassVehicleError ? "Vehicle Number is required" : undefined}
                  optionsType={gateOutVehicleList}
                  getOptionLabel={(option: any) => option?.vehicleNumber || ""}
                  value={
                    gateOutVehicleList.find(
                      (st: any) => String(st.vehicleId) === String(gatePassVehicleId)
                    ) || null
                  }
                  onChange={(option: any) => {
                    setGatePassVehicleId(String(option?.vehicleId ?? ""));
                    setGatePassVehicleError(false);
                  }}
                  loading={isGateOutDataLoading}
                />
              </Grid>
              {isBydGatePassInvoice && (
                <Grid item xs={12} className={classes.gatePassChassisField}>
                  <span className={classes.gatePassChassisLabel}>Chassis Number(s) *</span>
                  <Box className={classes.gatePassChassisMultiselectWrapper}>
                    <MultiSelect2
                      allValues={gatePassChassisOptions}
                      selctedItems={gatePassSelectedChassis}
                      displayValue="chasisNumber"
                      showSelectAll={gatePassChassisOptions.length > 0}
                      selectAllLabel="Select All"
                      closeOnSelect={false}
                      placeholder={
                        isGatePassChassisLoading ? "Loading..." : "Select Chassis Number(s)"
                      }
                      errorText={gatePassChassisError ? "Chassis Number is required" : undefined}
                      onSelect={(selectedList: any[]) => {
                        setGatePassSelectedChassis(selectedList);
                        setGatePassChassisNumber(
                          selectedList
                            .map((item) => item?.chasisNumber)
                            .filter(Boolean)
                            .join(", ")
                        );
                        setGatePassChassisError(false);
                      }}
                      onRemove={(selectedList: any[]) => {
                        setGatePassSelectedChassis(selectedList);
                        setGatePassChassisNumber(
                          selectedList
                            .map((item) => item?.chasisNumber)
                            .filter(Boolean)
                            .join(", ")
                        );
                        setGatePassChassisError(false);
                      }}
                    />
                  </Box>
                </Grid>
              )}

              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  className="customTextField"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label="Gate Pass Weight"
                  name="gatePassWeight"
                  placeholder="Enter Gate Pass Weight"
                  value={gatePassWeight}
                  onChange={(e) => setGatePassWeight(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  className="customTextField"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label="NLC Seal Number"
                  name="nlcSealNumber"
                  placeholder="Enter NLC Seal Number"
                  value={nlcSealNumber}
                  onChange={(e) => setNlcSealNumber(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Index Status"
                  placeholder="Select Index Status"
                  name="indexStatus"
                  required
                  error={indexStatusError}
                  helperText={indexStatusError ? "Index Status is required" : undefined}
                  optionsType={indexStatusList}
                  getOptionLabel={(option: any) => option.name || ""}
                  value={indexStatusList.find((opt: any) => opt.name === indexStatus) || null}
                  onChange={(option: any) => {
                    setIndexStatus(String(option?.name ?? ""));
                    setIndexStatusError(false);
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DuplicateReceiptDialog>

      <CommonDataGridPage
        module={moduleName}
        heading={pageHeading}
        GridDataComp={
          <CommonMaterialGrid
            rows={paymentRecord}
            columns={displayColumns}
            loader={loader}
            defaultPerPageRecord={15}
            height={550}
            isExport={isExport}
            deletedRow={true}
            exportHandler={exportHandler}
            filename={`Commercial_payment_list_${Date.now()}`}
          />
        }
      >
        <>
          <Grid spacing={2} container direction="row">
            <Grid item xs={12} md={3} lg={2}>
              <CommonAutoCompleteField
                cssClassName="customTextField"
                error={formik.touched.filterBy && Boolean(formik.errors.filterBy)}
                helperText={formik.touched.filterBy && formik.errors.filterBy}
                label="Filter by"
                placeholder="Filter by"
                name="filterBy"
                disableClearable={true}
                optionsType={filteryByArr}
                getOptionLabel={(option: any) => option.label || null}
                onChange={(row: any) => {
                  row ? setValues({ ...values, filterBy: row.value || "" }) : "";
                  if (row === null) {
                    formik.setFieldValue("filterBy", null);
                  }
                  filterByAction(row);
                }}
                value={
                  filteryByArr.find(({ value }: any) => value === formik.values.filterBy) || ""
                }
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2} className={!isCustomDate ? classes.hideDatesGrid : ""}>
              <CommonTextFieldDate
                cssClassName="customTextField"
                label={"Start Date"}
                name={"startDate"}
                datePickerRef={startDatePickerRef}
                value={formik.values.startDate}
                onChange={(e: any) => {
                  formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                  formik.setFieldValue("endDate", "");
                }}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              />
            </Grid>
            <Grid item xs={12} md={3} lg={2} className={!isCustomDate ? classes.hideDatesGrid : ""}>
              <CommonTextFieldDate
                cssClassName="customTextField"
                label={"End Date"}
                name={"endDate"}
                datePickerRef={endDatePickerRef}
                value={formik.values.endDate}
                onChange={(e: any) => {
                  formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                }}
                minDate={formik.values.startDate}
                maxDate={moment().format("YYYY-MM-DDTHH:mm")}
              />
            </Grid>
            <Grid item xs={12} md={isCustomDate ? 3 : 4} lg={2} style={{ paddingRight: 0 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formik.values.inProgressVehiclesDataOnly)}
                    onChange={formik.handleChange}
                    name="inProgressVehiclesDataOnly"
                    color="primary"
                  />
                }
                label="In Progress Vehicles"
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={isCustomDate ? 12 : 5}
              lg={isCustomDate ? 4 : 8}
              direction="row"
              // justify-content="flex-end"
              // alignItems="flex-end"
            >
              <Box
                textAlign="right"
                mt={1}
                mb={1}
                className="terminalChargesSearchBtns"
                style={{ float: "right" }}
              >
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetSearch()}>
                  Reset
                </CommonButton>
                <CommonButton cssClass="customSubmitBtn" isGreen onClick={handleSearch}>
                  Search
                </CommonButton>
                {terminalType !== 4 && (
                  <NewCommonButton
                    cssClass="customSubmitBtn"
                    mr0={true}
                    style={{ marginLeft: "10px" }}
                    onClick={() =>
                      window.open(
                        INVOICE_CLIMAX_URL,
                        "_blank",
                        "width=1300,height=600,left=150,top=100"
                      )
                    }
                  >
                    Old System Data
                  </NewCommonButton>
                )}
                {/* <MuiLink
                  to={terminalPageLink}
                  component={Link}
                  style={{ textDecoration: "none", marginBottom: "8px" }}
                >
                  <NewCommonButton
                    cssClass="customSubmitBtn"
                    isEdit={true}
                    mr0={true}
                    style={{ marginLeft: "10px" }}
                  >
                    Add Terminal Charges
                  </NewCommonButton>
                </MuiLink> */}
              </Box>
            </Grid>
          </Grid>
        </>
      </CommonDataGridPage>
      <TransitionDialog
        isOpen={isOpenDeleteDailog}
        handleClose={handleCloseDeleteDialog}
        heading={"Confirmation"}
        handleConfirm={handleDeleteDialog}
      >
        Are you sure you want to delete this record?
        <Box style={{ marginTop: "10px", marginBottom: "10px" }}>
          <TextField
            required
            multiline
            rows={1}
            rowsMax={5}
            size="small"
            fullWidth
            className="customTextField"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            label="Remarks"
            name="remarks"
            id="remarks"
            placeholder="Enter Remarks"
            disabled={false}
            value={deleteRemarks}
            onChange={(e) => {
              const { value } = e.target;
              setDeleteRemarks(value);
            }}
            error={isDeleteMode && !deleteRemarks}
            helperText={isDeleteMode && !deleteRemarks && "This field is required"}
          />
        </Box>
      </TransitionDialog>
      <TransitionDialog
        isOpen={isOpenVoidDailog}
        handleClose={handleCloseVoidDialog}
        heading={"Confirmation"}
        handleConfirm={handleVoidDialog}
      >
        Are you sure you want to void this record?
      </TransitionDialog>
    </>
  );
}
